import React, { useEffect, useMemo, useRef, useState } from "react";
import Trigger from "rc-trigger";
import { type } from "@testing-library/user-event/dist/type";
import { Dictionary } from "@/types/base";
import MessageItem from "./MessageItem";
import { CalcMessage, MessageComponent, StationMessageInfo } from "@/types/mySpace";
import { ReactComponent as GreenCheckIcon } from "@/assets/icons/header/green-check.svg";
import { ReactComponent as JingleIcon } from "@/assets/icons/message/jingle.svg";
import mockInfoList from "./mock";
import styles from "./style.module.scss";
import { useAsyncEffect, useMount } from "ahooks";
import { getImgDetail, messageList, opusExit, setMsgAllRead, setMsgRead } from "./api";
import { useHistory } from "react-router";
import { useOwnToast } from "@/utils/misc";

interface IStationMessage {
  something?: any;
  triggerStyle?: React.CSSProperties;
}

const StationMessage: React.FC<IStationMessage> = ({ triggerStyle = {} }) => {
  const history = useHistory();
  const toast = useOwnToast();
  const [mountTarget, setMountTarget] = useState<null | HTMLElement>(null);
  const [infoList, setInfoList] = useState<StationMessageInfo[]>([]);
  const [visible, setVisible] = useState<boolean>(false);
  const [unreadNum, setUnreadNum] = useState<number>(0);
  const triggerDom = useRef(null);

  useEffect(() => {
    if (triggerDom.current) setMountTarget(triggerDom.current);
  }, []);

  useAsyncEffect(async () => {
    getMsgInfo();
  }, []);

  const getMsgInfo = async () => {
    const res = await messageList({ page_index: 1, page_size: 100 });

    if (res.code === 0) {
      try {
        const parseList: StationMessageInfo[] = [];
        res.data.data.forEach((element: any) => {
          const parseItem = {
            ...element,
            content: JSON.parse(element.content),
          };
          parseList.push(parseItem);
        });
        setInfoList(parseList);
        setUnreadNum(res.data.unread_total || 0);
      } catch (e) {
        // do something
      }
    }
  };

  const calcMessage = (info: StationMessageInfo): CalcMessage => {
    const msgComp: MessageComponent[] = [];
    const textList = info.content.verb.split(/{|}/);
    const mapKeyList = Object.keys(info.content.replace || {}); // replace 有可能为null, Object.keys(null) 会直接崩溃
    textList.forEach((item, idx) => {
      if (item == "") return; // 不知道为什么 split 的结果数组第0个始终是一个 空字符串
      const pushItem: MessageComponent = {
        text: item,
        type: "normal",
      };
      if (mapKeyList.indexOf(item) !== -1) {
        const replaceItem = info.content.replace[item];
        pushItem.text = replaceItem.text;
        pushItem.type = "link";
        // 不同类型不同方法

        if (replaceItem.type === "member") {
          pushItem.onClick = handleMemberLinkClicked(replaceItem.link, info.id);
        }
        if (replaceItem.type === "opus") {
          pushItem.onClick = handleOpusLinkClicked(replaceItem.link, info.id);
        }
      }
      msgComp.push(pushItem);
    });
    const res: CalcMessage = {
      msgComp,
      iconType: info.icon_type,
      createTime: info.created_at,
      isRead: info.is_read,
      msgId: info.id,
    };
    return res;
  };

  const handleMemberLinkClicked = (userId: string | number, messageId: string | number) => {
    return async function (e: any) {
      e?.stopPropagation();
      setRead(messageId);
      window.open(`/artists/${String(userId)}`, "_blank");
      // history.push(`/artists/${String(userId)}`);
    };
  };

  const handleOpusLinkClicked = (ids: string | number, messageId: string | number) => {
    return async function (e: any) {
      e?.stopPropagation();
      setRead(messageId);
      const imgExistence = await verifyImgExistence(ids);
      if (imgExistence) {
        window.open(`/my_space?viewDetail=${String(ids)}`, "_blank");
      } else {
        toast({
          description: "作品被删除或者存在异常，无法查看",
          status: "warning",
          duration: 2500,
          position: "top",
        });
      }
    };
  };

  const verifyImgExistence = async (ids: string | number) => {
    const splitIds = String(ids).split("-");
    const opusId = splitIds[0] || "0";
    const imgId = splitIds[1] || "0";
    const res = await opusExit({ image_id: Number(imgId), opus_id: Number(opusId) });
    if (res?.code === 0 && res?.data) {
      return res?.data?.exist ?? false;
    } else {
      // err, do something
      return false;
    }
  };

  const setRead = async (messageId: string | number) => {
    await setMsgRead({ id: Number(messageId) });
    getMsgInfo();
  };

  const handleAllRead = async () => {
    await setMsgAllRead();
    getMsgInfo();
  };

  const content = useMemo(() => {
    return (
      <div className={styles["station-message-popup-content"]}>
        <div className={styles["popup-content-title"]}>
          <div className={styles["title-left"]}>消息</div>
          <div className={styles["title-right"]} onClick={handleAllRead}>
            设置全部为已读
          </div>
        </div>
        <div className={styles["popup-content-body"]}>
          {infoList.length === 0 ? (
            <div className={styles["no-data"]}>暂无消息</div>
          ) : (
            infoList.map((item, idx) => (
              <MessageItem key={idx} massage={calcMessage(item)} setRead={setRead} />
            ))
          )}
        </div>
      </div>
    );
  }, [infoList]);

  const handleVisibleChange = (visible: boolean) => {
    setVisible(visible);
  };
  const handleTriggerClicked = () => {
    setVisible(true);
  };

  return (
    <Trigger
      getPopupContainer={() => {
        return mountTarget ? mountTarget : document.body;
      }}
      popupVisible={visible}
      action={["click"]}
      popup={content}
      onPopupVisibleChange={handleVisibleChange}
      popupAlign={{
        points: ["tr", "br"],
        offset: [2, 13],
      }}
    >
      <div
        onClick={handleTriggerClicked}
        className={styles["station-message-trigger"]}
        ref={triggerDom}
      >
        <JingleIcon />
        {unreadNum > 0 && <div className={styles["unread-point"]}></div>}
      </div>
    </Trigger>
  );
};

export default StationMessage;
